/*
 * album/story.ts - the Story view's behaviour (§5, AL5). The chapters are server-rendered; this adds:
 *  - any photo tile opens the viewer on its list ([data-album-list] = a chapter's layout, a chip's subset,
 *    the guest wall, the grid) - at once on the tiles shown, then on every photo of its [data-album-scope]
 *    (chapter slug + optional data-album-sg sub-group, 0 = none; data-album-src, default pro) from /Gallery/Photos;
 *  - Highlight / Trip chips filter the mosaic in place (D1 / D13);
 *  - "📷 N guest photos from this moment" opens the viewer on that chapter's guest photos (D16b);
 *  - the guest wall's "Show more" pages in 24 more (D16a).
 */

import { AlbumData, Photo, UNGROUPED, chapterOf, fetchPhotos, fmt, photosOf, tile, tilt, toast } from "./shared";
import { Viewer, ViewerSource } from "./viewer";

/** Photos per /Gallery/Photos page when a Story tile loads its whole scope (GalleryController.GridPageMax). */
const SCOPE_PAGE = 120;

export function initStory(root: HTMLElement, data: AlbumData, viewer: Viewer, seeAll: (slug: string) => void): void {
  // --- tiles -> viewer (one delegated listener; covers tiles added later)
  root.addEventListener("click", (e) => {
    const me = e as MouseEvent;
    if (me.defaultPrevented || me.button !== 0 || me.ctrlKey || me.metaKey || me.shiftKey || me.altKey) return;
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-photo]");
    const list = a?.closest<HTMLElement>("[data-album-list]");
    if (!a || !list) return;
    const scope = a.closest<HTMLElement>("[data-album-scope]");
    const tiles = Array.from((scope && list.contains(scope) ? scope : list).querySelectorAll<HTMLAnchorElement>("a[data-photo]"));
    const photos: Photo[] = [];
    let at = 0;
    tiles.forEach((t) => {
      const p = data.photos[t.dataset.photo!];
      if (!p || photos.includes(p)) return;
      if (t === a) at = photos.length;
      photos.push(p);
    });
    if (!photos.length) return;
    e.preventDefault();
    viewer.open(photos, at);
    if (scope && list.contains(scope)) void openScope(scope, photos);
  });

  /** The tiles show a few picks; swap the viewer onto every photo of their chapter / sub-group (pages on demand). */
  async function openScope(scope: HTMLElement, shown: Photo[]): Promise<void> {
    const sg = scope.dataset.albumSg;
    const q = { g: scope.dataset.albumScope || null, sg: sg ? Number(sg) : null, src: scope.dataset.albumSrc || "pro" };
    const known = (p: Photo): Photo => (data.photos[p.id] = data.photos[p.id] ?? p);
    try {
      const page = await fetchPhotos({ ...q, skip: 0, take: SCOPE_PAGE });
      const items = page.items.map(known);
      let total = page.total;
      const source: ViewerSource = {
        total: () => total,
        more: async () => {
          const next = await fetchPhotos({ ...q, skip: items.length, take: SCOPE_PAGE });
          total = next.total;
          next.items.forEach((p) => items.push(known(p)));
          return next.items.length > 0;
        },
      };
      viewer.replace(shown, items, source);
    } catch {
      /* keep the tiles shown */
    }
  }

  // --- Highlight / Trip chips
  root.querySelectorAll<HTMLElement>("[data-album-chips]").forEach((bar) => {
    const chapter = chapterOf(data, bar.dataset.albumChips);
    const section = bar.closest<HTMLElement>(".album-chapter");
    const mosaic = section?.querySelector<HTMLElement>("[data-album-mosaic]");
    const note = section?.querySelector<HTMLElement>("[data-album-chip-note]");
    if (!chapter || !mosaic) return;
    const chips = Array.from(bar.querySelectorAll<HTMLButtonElement>("[data-album-chip]"));
    chips.forEach((chip) =>
      chip.addEventListener("click", () => {
        chips.forEach((c) => {
          const on = c === chip;
          c.classList.toggle("is-active", on);
          c.setAttribute("aria-pressed", on ? "true" : "false");
        });
        const sid = chip.dataset.albumChip ? Number(chip.dataset.albumChip) : null;
        const sub = sid == null ? undefined : chapter.subgroups.find((s) => s.id === sid);
        if (sub) mosaic.dataset.albumSg = String(sub.id);
        else delete mosaic.dataset.albumSg;
        const ids = sub ? sub.picks : chapter.picks;
        mosaic.replaceChildren(...photosOf(data, ids).map((p, i) => tile(p, `${i === 0 ? "album-tile--big " : ""}album-in`)));
        if (note) {
          note.textContent = sub?.note ?? "";
          note.hidden = !sub?.note;
        }
      }),
    );
  });

  // --- a chapter's guest photos (D16b), "See all N" -> Grid
  root.querySelectorAll<HTMLAnchorElement>("[data-album-guests]").forEach((a) =>
    a.addEventListener("click", (e) => {
      const c = chapterOf(data, a.dataset.albumGuests);
      const photos = c ? photosOf(data, c.guests) : [];
      if (!photos.length) return;
      e.preventDefault();
      viewer.open(photos, 0);
    }),
  );
  root.querySelectorAll<HTMLAnchorElement>("[data-album-seeall]").forEach((a) =>
    a.addEventListener("click", (e) => {
      e.preventDefault();
      seeAll(a.dataset.albumSeeall ?? UNGROUPED);
    }),
  );

  // --- guest wall paging (D16a)
  const wall = root.querySelector<HTMLElement>("[data-album-wall]");
  const more = root.querySelector<HTMLButtonElement>("[data-album-wall-more]");
  if (wall && more) {
    let shown = data.guestWall.ids.length;
    more.addEventListener("click", async () => {
      more.disabled = true;
      try {
        const page = await fetchPhotos({ src: "guest", skip: shown, take: data.guestWall.page });
        page.items.forEach((p) => {
          data.photos[p.id] = data.photos[p.id] ?? p;
          wall.appendChild(polaroid(p, data));
        });
        shown += page.items.length;
        more.hidden = shown >= page.total || page.items.length === 0;
      } catch {
        toast(data.i18n.loadFail);
      } finally {
        more.disabled = false;
      }
    });
  }
}

/** A guest-wall polaroid like _GuestWall.cshtml. */
function polaroid(p: Photo, data: AlbumData): HTMLElement {
  const fig = document.createElement("figure");
  fig.className = `album-polaroid album-polaroid--t${tilt(p.id)} album-in`;
  fig.appendChild(tile(p, "album-tile--polaroid"));
  const by = document.createElement("figcaption");
  by.className = "album-polaroid__by";
  by.textContent = fmt(data.i18n.by, p.by ?? "");
  fig.appendChild(by);
  if (p.caption && p.caption.trim()) {
    const n = document.createElement("p");
    n.className = "album-polaroid__note";
    n.textContent = p.caption;
    fig.appendChild(n);
  }
  return fig;
}
